import { useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Search, Edit, Trash2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import Dropdown from "../../components/dropdown/Index";
import { GetEmployee, DeleteEmployee } from "../../services/employee";
import useMallTenantDropdown from "../../hooks/useMallTenantDropdown";
import { cx, sortData, toLocalISOString } from "../../utils";
import { useNavigate } from "react-router-dom";

export interface ITenantEmployee {
  TenantEmployeeId: string;
  TenantId: number;
  MallId: number;
  BrandName: string;
  EmployeeNumber: string;
  CardUId: string | null;
  CardExpiredOn: string;
  IDNumber: string;
  FirstName: string;
  LastName: string;
  FullName: string;
  Gender: number;
  BirthOn: string;
  PhoneNumber: string;
  Email: string;
  Address: string | null;
  JobTitleId: number;
  JobTitle: string;
  EmployeeOn: string;
  VaccineStatusId: number;
  VaccineStatus: string;
  VaccineReason: string | null;
  EmployeeStatus: boolean;
  TenantFloor: string;
  AccessCardStatusId: number;
  AccessCardStatus: string;
  SyncOn: string;
  Status: number | string;
}

type SortKey =
  | "FullName"
  | "JobTitle"
  | "Status"
  | "IDNumber"
  | "EmployeeNumber";

export default function EmployeeInformationPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortKey, setSortKey] = useState<SortKey>("FullName");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [tenantEmployee, setTenantEmployee] = useState<ITenantEmployee[]>([]);
  const [jobTitleFilter, setJobTitleFilter] = useState<string>("All");
  const [statusFilter, setStatusFilter] = useState<string>("All");

  // ✅ Tambah mallId sebagai parameter
  const fetchTenantEmployee = async (
    tenantemployeeid: number,
    employeenumber: string,
    tenantid: number,
    mallid: number, // ← tambah ini
    number: number,
    page: number,
  ) => {
    try {
      const response = await GetEmployee(
        tenantemployeeid,
        employeenumber,
        tenantid,
        mallid, // ← kirim ke service
        number,
        page,
      );
      if (response.Code === 0) {
        setTenantEmployee(response.tenantEmployeeDatas);
      }
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error fetching tenant employee",
        text: String(error),
      });
    }
  };

  const {
    mallOptions,
    tenantOptions,
    selectedMall,
    selectedTenant,
    setSelectedMall,
    setSelectedTenant,
  } = useMallTenantDropdown((tenantId, mallId) => { 
    fetchTenantEmployee(0, "", tenantId, mallId, 0, 0); 
  });

  const filtered = useMemo(() => {
    const qq = q.toLowerCase().trim();
    const list: ITenantEmployee[] = Array.isArray(tenantEmployee) ? tenantEmployee : [];

    return list.filter((e) => {
      if (!e) return false;

      let inText = true;
      if (qq) {
        const fullName = (e.FullName || "").toLowerCase();
        const jobTitle = (e.JobTitle || "").toLowerCase();
        const email = (e.Email || "").toLowerCase();
        const phoneNumber = (e.PhoneNumber || "").toLowerCase();
        const idNumber = (e.IDNumber || "").toLowerCase();
        const employeeNumber = (e.EmployeeNumber || "").toLowerCase();

        inText =
          fullName.includes(qq) ||
          jobTitle.includes(qq) ||
          email.includes(qq) ||
          phoneNumber.includes(qq) ||
          idNumber.includes(qq) ||
          employeeNumber.includes(qq);
      }

      const matchJobTitle =
        jobTitleFilter === "All" || e.JobTitle === jobTitleFilter;

      const employeeStatus = String(e.Status);
      const matchStatus =
        statusFilter === "All" ||
        (statusFilter === "Active" && employeeStatus === "1") ||
        (statusFilter === "Inactive" && employeeStatus !== "1");

      return inText && matchJobTitle && matchStatus;
    });
  }, [tenantEmployee, q, jobTitleFilter, statusFilter]);

  const sorted = useMemo(
    () => sortData<ITenantEmployee>(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir],
  );

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const clampedPage = Math.min(page, totalPages);
  const start = (clampedPage - 1) * perPage;
  const end = Math.min(start + perPage, total);
  const pageRows = sorted.slice(start, end);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const onExportExcel = () => {
    try {
      const excelData = sorted.map((e, idx) => ({
        No: idx + 1,
        Name: e.FullName || "-",
        "Job Title": e.JobTitle || "-",
        NIK: e.IDNumber || "-",
        "Employee Card Number": e.EmployeeNumber || "-",
        "Phone Number": e.PhoneNumber || "-",
        "Employee Status": String(e.Status) === "1" ? "Active" : "Inactive",
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      ws["!cols"] = [
        { wch: 5 },
        { wch: 25 },
        { wch: 20 },
        { wch: 20 },
        { wch: 25 },
        { wch: 15 },
        { wch: 18 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Employees");

      const fileName = `Employee_Data_${toLocalISOString(new Date()).split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      Swal.fire({
        icon: "success",
        title: "Excel Export Success",
        text: `File ${fileName} has been downloaded`,
        timer: 2000,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Excel Export Failed",
        text: String(error),
      });
    }
  };

  const onExportPDF = () => {
    try {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Employee Information Report", 14, 15);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(`Generated: ${new Date().toLocaleDateString("en-GB")}`, 14, 22);

      const tableData = sorted.map((e, idx) => [
        idx + 1,
        e.FullName || "-",
        e.JobTitle || "-",
        e.IDNumber || "-",
        e.EmployeeNumber || "-",
        e.PhoneNumber || "-",
        String(e.Status) === "1" ? "Active" : "Inactive",
      ]);

      autoTable(doc, {
        head: [
          [
            "No",
            "Name",
            "Job Title",
            "NIK",
            "Employee Card Number",
            "Phone Number",
            "Status",
          ],
        ],
        body: tableData,
        startY: 28,
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 12, halign: "center" },
          1: { cellWidth: 45 },
          2: { cellWidth: 35 },
          3: { cellWidth: 35 },
          4: { cellWidth: 40 },
          5: { cellWidth: 30 },
          6: { cellWidth: 25, halign: "center" },
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        margin: { top: 28, left: 14, right: 14 },
      });

      const fileName = `Employee_Data_${toLocalISOString(new Date()).split("T")[0]}.pdf`;
      doc.save(fileName);

      Swal.fire({
        icon: "success",
        title: "PDF Export Success",
        text: `File ${fileName} has been downloaded`,
        timer: 2000,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "PDF Export Failed",
        text: String(error),
      });
    }
  };

  const onRemoveRow = async (id: string) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await DeleteEmployee(Number(id));

      if (response.Code === 0) {
        // ✅ Kirim selectedMall sebagai mallId
        fetchTenantEmployee(
          0,
          "",
          selectedTenant?.valueOf() || 0,
          selectedMall?.valueOf() || 0, // ← tambah ini
          0,
          0,
        );

        await Swal.fire({
          title: "Deleted!",
          text: "Employee has been deleted successfully.",
          icon: "success",
        });
      } else {
        await Swal.fire({
          icon: "error",
          title: "Failed to delete employee",
          text: response.Message || "Unknown error",
        });
      }
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Error deleting employee",
        text: String(error),
      });
    }
  };

  const jobTitles = ["All", "Store Manager", "Staff"];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <Dropdown
          mallSelected={setSelectedMall}
          mallOptions={mallOptions}
          tenantSelected={setSelectedTenant}
          tenantOptions={tenantOptions}
          mallValue={selectedMall}
          tenantValue={selectedTenant}
        />

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">

          <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">

              <div className="sm:col-span-1 lg:col-span-3">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Job Title
                </label>
                <div className="relative">
                  <select
                    value={jobTitleFilter}
                    onChange={(e) => {
                      setJobTitleFilter(e.target.value);
                      setPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
                  >
                    {jobTitles.map((title) => (
                      <option key={title} value={title}>
                        {title}
                      </option>
                    ))}
                  </select>
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>

              <div className="sm:col-span-1 lg:col-span-3">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Employee Status
                </label>
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
                  >
                    <option value="All">All</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
              </div>

              <div className="sm:col-span-2 lg:col-span-4">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => {
                      setQ(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Search by name, job title, NIK, phone..."
                    className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100 placeholder:text-gray-400"
                  />
                </div>
              </div>

              <div className="sm:col-span-2 lg:col-span-2">
                <button
                  onClick={() =>
                    navigate("form", {
                      state: { selectedMall, selectedTenant },
                    })
                  }
                  className="w-full px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
                >
                  Create
                </button>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <button
                onClick={onExportExcel}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors duration-200 shadow-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12.9,14.5L15.8,19H14L12,15.6L10,19H8.2L11.1,14.5L8.2,10H10L12,13.4L14,10H15.8L12.9,14.5Z" />
                </svg>
                Excel
              </button>
              <button
                onClick={onExportPDF}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-200 shadow-sm"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.92,12.31 10.92,12.31Z" />
                </svg>
                PDF
              </button>
            </div>
          </div>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="py-3 px-4 text-left">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">No</span>
                  </th>
                  <Th sortable activeKey={sortKey} dir={sortDir} k="FullName" onClick={toggleSort}>Name</Th>
                  <Th sortable activeKey={sortKey} dir={sortDir} k="JobTitle" onClick={toggleSort}>Job Title</Th>
                  <Th sortable activeKey={sortKey} dir={sortDir} k="IDNumber" onClick={toggleSort}>NIK</Th>
                  <Th sortable activeKey={sortKey} dir={sortDir} k="EmployeeNumber" onClick={toggleSort}>Employee Card Number</Th>
                  <th className="py-3 px-4 text-left">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Phone Number</span>
                  </th>
                  <Th sortable activeKey={sortKey} dir={sortDir} k="Status" onClick={toggleSort}>Employee Status</Th>
                  <th className="py-3 px-4 text-center">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {pageRows.map((e, idx) => (
                  <tr
                    key={e.TenantEmployeeId}
                    className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
                  >
                    <td className="py-4 px-4 text-sm text-gray-900 dark:text-gray-100">{start + idx + 1}</td>
                    <td className="py-4 px-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{e.FullName}</div>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300">{e.JobTitle || "-"}</td>
                    <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300 font-mono">{e.IDNumber || "-"}</td>
                    <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300 font-mono">{e.EmployeeNumber || "-"}</td>
                    <td className="py-4 px-4 text-sm text-gray-700 dark:text-gray-300">{e.PhoneNumber || "-"}</td>
                    <td className="py-4 px-4">
                      <span
                        className={cx(
                          "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium",
                          String(e.Status) === "1"
                            ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                            : "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                        )}
                      >
                        {String(e.Status) === "1" ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() =>
                            navigate("form", {
                              state: {
                                selectedMall,
                                selectedTenant,
                                employeeId: e.TenantEmployeeId,
                              },
                            })
                          }
                          className="p-2 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onRemoveRow(e.TenantEmployeeId)}
                          className="p-2 text-red-600 dark:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {pageRows.length === 0 && (
                  <tr>
                    <td className="py-12 px-4 text-center text-sm text-gray-500 dark:text-gray-400" colSpan={8}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-800">
            {pageRows.length === 0 ? (
              <div className="py-12 px-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No data available
              </div>
            ) : (
              pageRows.map((e, idx) => (
                <div
                  key={e.TenantEmployeeId}
                  className="p-4 space-y-3 hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 text-xs font-semibold flex items-center justify-center">
                        {start + idx + 1}
                      </span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {e.FullName}
                      </span>
                    </div>
                    <span
                      className={cx(
                        "flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium",
                        String(e.Status) === "1"
                          ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                          : "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                      )}
                    >
                      {String(e.Status) === "1" ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Job Title</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{e.JobTitle || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Phone Number</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{e.PhoneNumber || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">NIK</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 font-mono">{e.IDNumber || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Employee Card No</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 font-mono">{e.EmployeeNumber || "-"}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() =>
                        navigate("form", {
                          state: {
                            selectedMall,
                            selectedTenant,
                            employeeId: e.TenantEmployeeId,
                          },
                        })
                      }
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      <Edit className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => onRemoveRow(e.TenantEmployeeId)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Show result:</span>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-1.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
              >
                {[2, 5, 10, 20, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={clampedPage === 1}
                className={cx(
                  "px-3 py-1.5 text-sm rounded-lg border transition-colors",
                  clampedPage === 1
                    ? "text-gray-300 bg-gray-50 border-gray-200 cursor-not-allowed dark:text-gray-600 dark:bg-gray-800 dark:border-gray-700"
                    : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800",
                )}
              >
                &lt;
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (clampedPage <= 3) {
                  pageNum = i + 1;
                } else if (clampedPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = clampedPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={cx(
                      "w-9 h-9 text-sm rounded-lg transition-colors",
                      pageNum === clampedPage
                        ? "bg-blue-600 text-white"
                        : "text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800",
                    )}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && clampedPage < totalPages - 2 && (
                <>
                  <span className="text-gray-400">...</span>
                  <button
                    onClick={() => setPage(totalPages)}
                    className="w-9 h-9 text-sm rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={clampedPage === totalPages}
                className={cx(
                  "px-3 py-1.5 text-sm rounded-lg border transition-colors",
                  clampedPage === totalPages
                    ? "text-gray-300 bg-gray-50 border-gray-200 cursor-not-allowed dark:text-gray-600 dark:bg-gray-800 dark:border-gray-700"
                    : "text-gray-700 bg-white border-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800",
                )}
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({
  children,
  sortable,
  k,
  activeKey,
  dir,
  onClick,
}: {
  children: React.ReactNode;
  sortable?: boolean;
  k?: SortKey;
  activeKey?: SortKey;
  dir?: "asc" | "desc";
  onClick?: (k: SortKey) => void;
}) {
  const isActive = sortable && k === activeKey;
  return (
    <th
      className={cx(
        "py-3 px-4 text-left select-none",
        sortable && "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800",
      )}
      onClick={sortable && k && onClick ? () => onClick(k as SortKey) : undefined}
    >
      <div className="inline-flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{children}</span>
        {sortable && (
          <svg
            className={cx("w-3 h-3", isActive ? "text-gray-600 dark:text-gray-300" : "text-gray-400")}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            {isActive ? (
              dir === "asc" ? <path d="M5 15l7-7 7 7" /> : <path d="M19 9l-7 7-7-7" />
            ) : (
              <>
                <path d="M5 15l7-7 7 7" />
                <path d="M5 9l7 7 7-7" opacity="0.4" />
              </>
            )}
          </svg>
        )}
      </div>
    </th>
  );
}